export const BEFORE_APP_UPDATE_EVENT = 'cardreplay:before-app-update'

const UPDATE_ACTIVATION_TIMEOUT_MS = 15_000

type AppUpdateOptions = Readonly<{
  serviceWorker?: ServiceWorkerContainer | null
  reload?: () => void
  timeoutMs?: number
}>

function waitForUpdatedWorker(serviceWorker: ServiceWorkerContainer) {
  let settled = false
  let registration: ServiceWorkerRegistration | undefined
  let worker: ServiceWorker | null = null
  let timeoutId: number | undefined
  let resolveChange!: () => void
  let rejectChange!: (error: Error) => void
  const promise = new Promise<void>((resolve, reject) => {
    resolveChange = resolve
    rejectChange = reject
  })

  const cleanup = () => {
    serviceWorker.removeEventListener('controllerchange', complete)
    registration?.removeEventListener('updatefound', observeWorker)
    worker?.removeEventListener('statechange', checkWorker)
    window.clearTimeout(timeoutId)
  }
  function complete() {
    if (settled) return
    settled = true
    cleanup()
    resolveChange()
  }
  function fail() {
    if (settled) return
    settled = true
    cleanup()
    rejectChange(new Error('The updated service worker did not activate'))
  }
  function checkWorker() {
    if (worker?.state === 'activated') complete()
    else if (worker?.state === 'redundant') fail()
    else if (worker?.state === 'installed') {
      worker.postMessage({ type: 'SKIP_WAITING' })
    }
  }
  function observeWorker() {
    const nextWorker = registration?.waiting ?? registration?.installing ?? null
    if (worker === nextWorker) return
    worker?.removeEventListener('statechange', checkWorker)
    worker = nextWorker
    worker?.addEventListener('statechange', checkWorker)
    checkWorker()
  }

  serviceWorker.addEventListener('controllerchange', complete)
  return {
    promise,
    cancel: cleanup,
    observe(
      nextRegistration: ServiceWorkerRegistration | undefined,
      timeoutMs: number,
    ) {
      if (settled) return
      registration = nextRegistration
      registration?.addEventListener('updatefound', observeWorker)
      observeWorker()
      if (settled) return
      // With clientsClaim=false activation need not change this document's
      // controller. A document reload will attach the activated worker.
      if (!worker) complete()
      else timeoutId = window.setTimeout(fail, timeoutMs)
    },
  }
}

export async function applyAppUpdate(
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>,
  options: AppUpdateOptions = {},
) {
  const updateEvent = new CustomEvent(BEFORE_APP_UPDATE_EVENT, {
    cancelable: true,
  })
  if (!window.dispatchEvent(updateEvent)) return false

  const serviceWorker =
    options.serviceWorker === undefined
      ? 'serviceWorker' in navigator
        ? navigator.serviceWorker
        : null
      : options.serviceWorker
  const activation = serviceWorker ? waitForUpdatedWorker(serviceWorker) : null
  try {
    await updateServiceWorker(false)
    if (serviceWorker && activation) {
      activation.observe(
        await serviceWorker.getRegistration(),
        options.timeoutMs ?? UPDATE_ACTIVATION_TIMEOUT_MS,
      )
      await activation.promise
    }
    // A fresh document rebuilds the viewport and fixed layers. Keep the same
    // route and existing scroll restoration, so the cached update stays gentle.
    if (document.activeElement instanceof HTMLElement)
      document.activeElement.blur()
    ;(options.reload ?? (() => window.location.reload()))()
    return true
  } catch (error) {
    activation?.cancel()
    throw error
  }
}
